const LOAD_MORE_THRESHOLD_PX = 96;
const LOAD_MORE_THRESHOLD_RATIO = 0.25;
const HORIZONTAL_LOAD_MORE_THRESHOLD_RATIO = 0.35;
const URL_SCOPE_RESOLVERS = Object.freeze({
    api: "__fishystuffResolveApiUrl",
    site: "__fishystuffResolveSiteUrl",
    cdn: "__fishystuffResolveCdnUrl",
});

export function getStringAttribute(element, name) {
    return String(element.getAttribute(name) ?? "").trim();
}

export function setStringAttribute(element, name, value) {
    const normalized = String(value ?? "");
    if (normalized) {
        element.setAttribute(name, normalized);
        return;
    }
    element.removeAttribute(name);
}

export function upgradeProperty(element, property) {
    if (!Object.prototype.hasOwnProperty.call(element, property)) {
        return;
    }
    const value = element[property];
    delete element[property];
    element[property] = value;
}

export function dispatchValueEvents(element) {
    element.dispatchEvent(new Event("input", { bubbles: true }));
    element.dispatchEvent(new Event("change", { bubbles: true }));
}

export function cloneChildNodes(source) {
    return Array.from(source.childNodes, (node) => node.cloneNode(true));
}

export function findPropertyDescriptor(target, property) {
    let current = target;
    while (current) {
        const descriptor = Object.getOwnPropertyDescriptor(current, property);
        if (descriptor) {
            return descriptor;
        }
        current = Object.getPrototypeOf(current);
    }
    return null;
}

export function normalizeSearchText(value) {
    return String(value ?? "").trim().toLowerCase();
}

function loadMoreThreshold(maxScrollTop) {
    return Math.min(LOAD_MORE_THRESHOLD_PX, Math.max(0, maxScrollTop) * LOAD_MORE_THRESHOLD_RATIO);
}

function horizontalLoadMoreThreshold(maxScrollLeft) {
    return Math.max(
        LOAD_MORE_THRESHOLD_PX,
        Math.max(0, maxScrollLeft) * HORIZONTAL_LOAD_MORE_THRESHOLD_RATIO,
    );
}

export function scrollMetrics(element) {
    const maxScrollTop = Math.max(0, Number(element?.scrollHeight || 0) - Number(element?.clientHeight || 0));
    const maxScrollLeft = Math.max(0, Number(element?.scrollWidth || 0) - Number(element?.clientWidth || 0));
    return {
        maxScrollTop,
        maxScrollLeft,
        scrollTop: Math.max(0, Number(element?.scrollTop || 0)),
        scrollLeft: Math.max(0, Number(element?.scrollLeft || 0)),
    };
}

export function hasScrollableRange(metrics) {
    return metrics.maxScrollTop > 0 || metrics.maxScrollLeft > 0;
}

export function isNearScrollEnd(metrics) {
    const remainingY = metrics.maxScrollTop - metrics.scrollTop;
    const remainingX = metrics.maxScrollLeft - metrics.scrollLeft;
    return (
        (metrics.maxScrollTop > 0 && remainingY <= loadMoreThreshold(metrics.maxScrollTop))
        || (metrics.maxScrollLeft > 0 && remainingX <= horizontalLoadMoreThreshold(metrics.maxScrollLeft))
    );
}

function isScrollableOverflowValue(value) {
    const normalized = String(value ?? "").trim().toLowerCase();
    return normalized === "auto" || normalized === "scroll" || normalized === "overlay";
}

export function resolveSearchResultsScrollHost(results, panel) {
    if (!(results instanceof HTMLElement)) {
        return null;
    }
    let fallback = results;
    let current = results;
    while (current instanceof HTMLElement) {
        const style = typeof window.getComputedStyle === "function" ? window.getComputedStyle(current) : null;
        const overflowY = style?.overflowY ?? style?.overflow ?? "";
        const overflowX = style?.overflowX ?? style?.overflow ?? "";
        if (isScrollableOverflowValue(overflowY) || isScrollableOverflowValue(overflowX)) {
            fallback = current;
            if (hasScrollableRange(scrollMetrics(current))) {
                return current;
            }
        }
        if (current === panel) {
            break;
        }
        current = current.parentElement ?? (current.parentNode instanceof HTMLElement ? current.parentNode : null);
    }
    return fallback;
}

export function reconcilePickerPanel(owner, currentPanel, detached) {
    const renderedPanel = owner.querySelector?.('[data-role="panel"]') ?? null;
    if (detached && currentPanel instanceof HTMLElement) {
        if (renderedPanel instanceof HTMLElement && renderedPanel !== currentPanel) {
            renderedPanel.parentNode?.removeChild?.(renderedPanel);
        }
        return currentPanel;
    }
    if (currentPanel instanceof HTMLElement && owner.contains?.(currentPanel)) {
        return currentPanel;
    }
    return renderedPanel instanceof HTMLElement ? renderedPanel : null;
}

export function searchResultsAreNearEnd(element) {
    return element instanceof HTMLElement && isNearScrollEnd(scrollMetrics(element));
}

export function resolveScopedUrl(rawUrl, scope) {
    const normalizedUrl = String(rawUrl ?? "").trim();
    if (!normalizedUrl) {
        return "";
    }
    if (
        normalizedUrl.startsWith("http://")
        || normalizedUrl.startsWith("https://")
        || normalizedUrl.startsWith("data:")
    ) {
        return normalizedUrl;
    }

    const resolverName = URL_SCOPE_RESOLVERS[String(scope ?? "").trim()];
    if (resolverName && typeof window[resolverName] === "function") {
        return window[resolverName](normalizedUrl);
    }

    return new URL(normalizedUrl, window.location.href).toString();
}
